<script setup lang="ts">
import type { z } from 'zod'
import type { Country, FormSubmit } from '~/types'
import { gsap } from 'gsap'

definePageMeta({
  layout: 'landing',
  smoothScroll: true,
})

const { t, locale } = useI18n()
const toast = useToast()
const localePath = useLocalePath()
const { contactSales } = usePostRequests()
const { contactSalesSchema } = useValidation()
const { checkDigit } = useFormater()
const { countries } = storeToRefs(useAccountStore())
const { getCountries } = useProfile()

const { crumbs } = useBreadcrumbs([
  { label: t('label.home'), to: '/' },
  { label: t('label.contact_sales') },
])

useHead({
  titleTemplate: (chunk) => chunk || 'bookforme',
  title: () => `bookforme — ${t('page.contact_sales.title')}`,
  meta: [
    { name: 'description', content: 'Contact the bookforme sales team. We\'ll answer your questions and help you find the right plan for your business.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: () => `bookforme — ${t('page.contact_sales.title')}` },
    { property: 'og:description', content: 'We\'re happy to answer your sales questions.' },
    { property: 'og:site_name', content: 'bookforme' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
})

type Schema = z.output<typeof contactSalesSchema>

const form = ref(null)
const selectedCountry = ref<undefined | Country>(undefined)

const formData = ref<Schema>({
  name: '',
  email: '',
  message: '',
  country_id: selectedCountry.value?.id as number,
  phone: '',
  business_name: '',
  team_size: '',
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSuccess = (response: any) => {
  formData.value.name = ''
  formData.value.email = ''
  formData.value.message = ''
  formData.value.phone = ''
  formData.value.business_name = ''
  formData.value.team_size = ''
  toast.add({
    title: response.message,
    color: 'green',
    icon: 'i-approved-success',
  })
}

watch(
  selectedCountry,
  () => (formData.value.country_id = selectedCountry.value?.id || 0),
  { deep: true },
)

const { submit, inProgress } = useSubmit<Schema>(
  () => contactSales(formData.value),
  { onSuccess },
  form as FormSubmit<Schema>,
)

const labelUi = {
  label: { base: 'block text-sm font-bold text-gray-900 mb-1.5' },
  error: 'mt-1 text-xs text-red-500',
}

const inputUi = {
  base: 'w-full bg-white border border-gray-300 rounded-xl shadow-none focus:ring-0 focus:border-deep-indigo',
  rounded: 'rounded-xl',
  padding: { xl: 'px-4 py-4' },
}

try {
  const response = await getCountries()
  countries.value = response.data.countries
  selectedCountry.value = countries.value[0]
}
catch (err) {
  console.error(err)
}

onMounted(() => {
  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
  tl.fromTo('.cs-header',
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0, duration: 0.85 },
  ).fromTo('.cs-card',
    { opacity: 0, y: 40 },
    { opacity: 1, y: 0, duration: 0.85 },
    '-=0.65',
  )
})
</script>

<template>
  <section class="bg-white py-24 md:pt-28 lg:pt-44 lg:pb-28">
    <div class="container">

      <CommonBreadcrumbs :items="crumbs" />

      <!-- Header -->
      <div class="cs-header text-center mb-12 md:mb-16">
        <div class="bfm-badge bg-creamy-yellow text-gray-900 px-3 md:px-4 py-1.5 md:py-2 text-xs sm:text-sm md:text-base mb-6 inline-flex">
          <UIcon name="i-heroicons-user-group-20-solid" class="text-heart-purple size-3.5 sm:size-4 md:size-5" />
          {{ t('label.contact_sales') }}
        </div>
        <h1 class="text-[clamp(1.75rem,4vw,3rem)] font-bold leading-[1.2] rtl:leading-[1.5] text-deep-indigo mb-5 md:mb-6">
          {{ t('page.contact_sales.title') }}
        </h1>
        <p class="text-sm sm:text-base lg:text-lg font-medium text-overcast-night leading-relaxed max-w-2xl mx-auto">
          {{ t('page.contact_sales.desc') }}
        </p>
      </div>

      <!-- Form card -->
      <div class="cs-card max-w-3xl mx-auto">
        <div class="rounded-3xl border-2 border-gray-900 shadow-[10px_10px_0_#111827] bg-amber-50 p-8 md:p-11 lg:p-12">

          <p class="text-sm md:text-base text-gray-500 mb-8 md:mb-10">
            {{ t('page.contact_sales.sub_desc') }}
          </p>

          <UForm
            ref="form"
            :state="formData"
            :schema="contactSalesSchema"
            :validate-on="['submit']"
            class="flex flex-col gap-6"
            @submit="submit">

            <!-- Row 1: Name + Email -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UFormGroup :label="t('label.your_name')" name="name" :ui="labelUi">
                <UInput
                  v-model="formData.name"
                  type="text"
                  :placeholder="t('placeholder.your_name')"
                  color="white"
                  size="xl"
                  :ui="inputUi"
                />
              </UFormGroup>
              <UFormGroup :label="t('label.email')" name="email" :ui="labelUi">
                <UInput
                  v-model="formData.email"
                  type="text"
                  placeholder="e.g. john@mail.com"
                  dir="ltr"
                  :class="{ 'placeholder-right': locale === 'ar' }"
                  color="white"
                  size="xl"
                  :ui="inputUi"
                />
              </UFormGroup>
            </div>

            <!-- Phone with country selector -->
            <UFormGroup :label="t('label.phone_number')" name="phone" :ui="labelUi" class="relative z-20">
              <UInput
                v-model="formData.phone"
                type="text"
                variant="outline"
                placeholder="000 00 000"
                dir="ltr"
                size="xl"
                inputmode="numeric"
                :ui="{
                  padding: { xl: 'px-3.5 py-3' },
                  leading: { padding: { xl: 'ps-27.5' } },
                  icon: {
                    leading: {
                      pointer: '',
                      wrapper: 'flex item-center absolute top-1/2 -translate-y-2/4 ltr:start-0 rtl:end-0 rtl:pe-3 rtl:ps-0 ltr:pe-0',
                    },
                  },
                }"
                @keydown="checkDigit">
                <template #leading>
                  <div class="flex items-center" dir="ltr">
                    <UFormGroup name="country_id">
                      <USelectMenu
                        v-model="selectedCountry"
                        :options="countries"
                        :disabled="inProgress"
                        :popper="{ placement: 'bottom-start' }"
                        :padded="false"
                        size="xl"
                        :ui="{
                          trailing: { padding: { xl: 'pe-6' } },
                          color: { white: { outline: 'text-gray-900' } },
                          icon: { trailing: { padding: { xl: 'py-3 px-0' } } },
                        }"
                        :ui-menu="{ width: 'w-80' }">
                        <template #label>
                          <div class="flex items-center overflow-hidden">
                            <img
                              v-if="selectedCountry?.image"
                              class="size-6 rounded-md me-1.5"
                              :src="selectedCountry?.image"
                              :alt="selectedCountry?.name">
                            <span class="inline-block text-center min-w-9.5">+{{ selectedCountry?.dialing_code }}</span>
                          </div>
                        </template>
                        <template #option="{ option }">
                          <div class="flex items-center overflow-hidden">
                            <img
                              class="size-6 rounded-md"
                              :src="option?.image"
                              :alt="option?.name">
                            <span class="capitalize ml-1.5 mr-1">{{ option?.name }}</span>
                            <span class="inline-block">(+{{ option?.dialing_code }})</span>
                          </div>
                        </template>
                      </USelectMenu>
                    </UFormGroup>
                  </div>
                </template>
              </UInput>
            </UFormGroup>

            <!-- Row 2: Business Name + Team Size -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UFormGroup :label="t('label.business_name')" name="business_name" :ui="labelUi">
                <UInput
                  v-model="formData.business_name"
                  type="text"
                  :placeholder="t('placeholder.business_name')"
                  color="white"
                  size="xl"
                  :ui="inputUi"
                />
              </UFormGroup>
              <UFormGroup :label="t('label.team_size')" name="team_size" :ui="labelUi">
                <UInput
                  v-model="formData.team_size"
                  type="text"
                  :placeholder="t('placeholder.team_size')"
                  dir="ltr"
                  :class="{ 'placeholder-right': locale === 'ar' }"
                  color="white"
                  size="xl"
                  :ui="inputUi"
                  @keydown="checkDigit"
                />
              </UFormGroup>
            </div>

            <!-- Message -->
            <UFormGroup :label="t('label.message')" name="message" :ui="labelUi">
              <CommonTextarea
                v-model="formData.message"
                :rows="8"
                :max-length="250"
                :placeholder="t('placeholder.contact_sales_message')"
              />
            </UFormGroup>

            <!-- Privacy note -->
            <p class="text-xs text-gray-400 leading-relaxed -mt-1">
              {{ t('page.contact_sales.note') }}
              <NuxtLink :to="localePath('/privacy-policy')" class="underline hover:text-gray-600">
                {{ t('page.contact_sales.privacy_policy') }}
              </NuxtLink>
            </p>

            <!-- Submit -->
            <button
              type="submit"
              :disabled="inProgress"
              class="bfm-btn bfm-btn-lg w-full bg-cheese-yellow disabled:opacity-60 disabled:cursor-not-allowed">
              <UIcon v-if="inProgress" name="i-heroicons-arrow-path-20-solid" class="animate-spin size-5" />
              {{ t('label.submit') }}
            </button>

          </UForm>
        </div>
      </div>

    </div>
  </section>
</template>
